import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "../AuthContext";
import { useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";

import {
  getTableItems,
  addProductToTable,
  updateTableItem,
  deleteTableItem,
  getProducts,
  getTables,
} from "../api";

export default function TableItems() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();

  const [tabela, setTabela] = useState(null);
  const [items, setItems] = useState([]);
  const [produtos, setProdutos] = useState([]);

  // Form de adicionar novo item
  const [produtoId, setProdutoId] = useState("");
  const [valor, setValor] = useState("");

  // Controle de edição inline
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null); // para feedback de salvando

  // Carregar dados iniciais
  useEffect(() => {
    async function loadInitial() {
      const [tb, pr] = await Promise.all([
        getTables(user.token),
        getProducts(user.token),
      ]);

      const tabelaAtual = tb.find((t) => String(t.id) === String(id));
      setTabela(tabelaAtual);
      setProdutos(pr);
    }
    loadInitial();
  }, [user.token, id]);

  // Carregar itens da tabela
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTableItems(user.token, id);
      setItems(data);
    } catch (err) {
      alert("Erro ao carregar itens");
    } finally {
      setLoading(false);
    }
  }, [user.token, id]);

  useEffect(() => {
    if (id) loadItems();
  }, [id, loadItems]);

  // Formatar valor como moeda (pt-BR)
  const formatCurrency = (value) => {
    return Number(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrency = (str) => {
    return parseFloat(str.replace(/\D/g, "")) / 100;
  };

  // Adicionar item
  const handleAdd = async () => {
    if (!produtoId || !valor) return alert("Preencha produto e valor");

    setLoading(true);
    try {
      await addProductToTable(user.token, {
        tabela_id: id,
        produto_id: produtoId,
        valor: parseCurrency(valor),
      });

      setProdutoId("");
      setValor("");
      loadItems();
    } catch (err) {
      alert(err.message || "Erro ao adicionar item");
    } finally {
      setLoading(false);
    }
  };

  // Iniciar edição
  const startEditing = (item) => {
    setEditingId(item.id);
    setEditValue(formatCurrency(item.valor));
  };

  // Salvar edição
  const handleSaveEdit = async (itemId) => {
    if (!editValue) return;

    setSavingId(itemId);
    try {
      await updateTableItem(itemId, user.token, parseCurrency(editValue));
      setEditingId(null);
      loadItems();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Deseja realmente remover este item?")) return;

    try {
      await deleteTableItem(user.token, itemId);
      loadItems();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card" style={{ maxWidth: "900px", margin: "0 auto" }}>

          <div style={{justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
            <h3>
              Itens da Tabela {tabela && <small> — {tabela.nome}</small>}
            </h3>
          </div>

          {/* ==================== ADICIONAR NOVO ITEM ==================== */}
          <div className="item-row add-row" style={{ marginBottom: "2.5rem", gridTemplateColumns: "2fr 1fr auto"}}>
            <div className="item-input">
              <label>Produto</label>
              <select value={produtoId} onChange={(e) => setProdutoId(e.target.value)}>
                <option value="">Selecione um produto</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo} - {p.referencia}
                  </option>
                ))}
              </select>
            </div>

            <div className="item-input">
              <label>Valor (R$)</label>
              <input
                type="text"
                placeholder="0,00"
                value={valor}
                onChange={(e) => {
                  let v = e.target.value.replace(/\D/g, "");
                  v = (Number(v) / 100).toFixed(2);
                  setValor(v.replace(".", ","));
                }}
              />
            </div>

            <div className="item-actions">
              <button
                className="btn-small"
                onClick={handleAdd}
                disabled={loading || !produtoId || !valor}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* ==================== LISTA DE ITENS ==================== */}
          {loading && <p>Carregando...</p>}

          {items.length === 0 && !loading && (
            <p style={{ opacity: 0.7, textAlign: "center", padding: "2rem" }}>
              Nenhum item cadastrado nesta tabela.
            </p>
          )}

          <div className="items-list">
            {items.map((item) => (
              <div key={item.id} className="item-row"  style={{ gridTemplateColumns: "2fr 1fr auto"}}>
                <div className="item-input">
                  <label>Produto</label>
                  <input value={item.codigo + " - " + item.referencia} disabled />
                </div>

                <div className="item-input">
                  <label>Valor (R$)</label>
                  {editingId === item.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, "");
                        v = (Number(v) / 100).toFixed(2);
                        setEditValue(v.replace(".", ","));
                      }}
                      autoFocus
                    />
                  ) : (
                    <input value={formatCurrency(item.valor)} disabled />
                  )}
                </div>

                <div className="item-actions">
                  {editingId === item.id ? (
                    <>
                      <button
                        className="btn-small"
                        onClick={() => handleSaveEdit(item.id)}
                        disabled={savingId === item.id}
                      >
                        {savingId === item.id ? "Salvando..." : <> Salvar</>}
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => setEditingId(null)}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-small"
                        onClick={() => startEditing(item)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-danger btn-small"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}